import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TFilters } from './types-filters';
import { TAppConfig } from '../api-ducks/app-config-duck/types-app-config';
import { TParticipation } from '../api-ducks/participation-details-duck/types-participation-details';
import { defaultFilters, initialState } from './utils-filters';
import { ETabId } from '../../services/tab-configurations/tab-types';
import { TPaymentDocument, TPaymentPartition, TPaymentPartitionGroup } from '../api-ducks/earnings-duck/types-earnings';

type TDefaultFilterParams = {
  appConfig: TAppConfig;
  participationDetails: TParticipation;
  isImpersonating?: boolean;
};

const filtersDuck = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TFilters>) => ({
      ...state,
      ...action.payload,
    }),
    setActiveTabFilter: (state, action: PayloadAction<ETabId>) => ({
      ...state,
      activeTab: action.payload,
    }),
    setSelectedPaymentPartition: (state, action: PayloadAction<TPaymentPartition | undefined>) => ({
      ...state,
      selectedPaymentPartition: action.payload,
    }),
    setSelectedPaymentPartitionGroup: (state, action: PayloadAction<TPaymentPartitionGroup | undefined>) => ({
      ...state,
      selectedPaymentPartitionGroup: action.payload,
    }),
    addAvailablePaymentPartitions: (state, action: PayloadAction<TPaymentPartitionGroup>) => {
      let availablePaymentPartitions = state.availablePaymentPartitions || [];
      // If the current payments partitions are for a different user (impersonation) we clear the state first.
      if (state.availablePaymentPartitions && state.availablePaymentPartitions[0]?.partitions[0]?.personnelNumber !== state.personnelNumber) {
        availablePaymentPartitions = [];
      }
      if (!availablePaymentPartitions.find((p) => p.partitionGroup === action.payload.partitionGroup)) {
        return {
          ...state,
          availablePaymentPartitions: [...availablePaymentPartitions, action.payload],
        };
      }
      return state;
    },
    hydratePaymentPartition: (state, action: PayloadAction<TPaymentDocument>) => {
      const newAvailablePaymentPartitions = state.availablePaymentPartitions || [];
      let newSelectedPaymentPartition = state.selectedPaymentPartition || ({} as TPaymentPartition);
      const availablePaymentPartitions = newAvailablePaymentPartitions.map((partitionGroup) => {
        if (
          partitionGroup.partitionGroup === `${action.payload.fiscalYearId}` ||
          partitionGroup.partitionGroup === `${action.payload.fiscalSemesterId}`
        ) {
          const newPartitions = partitionGroup.partitions;
          const partitions = newPartitions.map((partition) => {
            if (partition.id === action.payload.id) {
              const hydratedPartition = {
                ...partition,
                roleSummary: action.payload.earningDetails.map((details) => details.roleSummary).join(', '),
              };
              if (newSelectedPaymentPartition.id === hydratedPartition.id) {
                newSelectedPaymentPartition = { ...hydratedPartition };
              }
              return { ...hydratedPartition };
            }
            return partition;
          });
          return {
            ...partitionGroup,
            partitions,
          };
        }
        return partitionGroup;
      });
      return {
        ...state,
        availablePaymentPartitions,
        selectedPaymentPartition: newSelectedPaymentPartition,
      };
    },
    setDefaultFilters: (state, action: PayloadAction<TDefaultFilterParams>) => {
      const defFilters = defaultFilters(action.payload.appConfig, action.payload.participationDetails, state.availablePaymentPartitions);
      return {
        ...state,
        ...defFilters,
      };
    },

    clearFilters: () => initialState,
  },
});

export const {
  setFilters,
  setDefaultFilters,
  setActiveTabFilter,
  setSelectedPaymentPartition,
  setSelectedPaymentPartitionGroup,
  hydratePaymentPartition,
  addAvailablePaymentPartitions,
  clearFilters,
} = filtersDuck.actions;
export default filtersDuck.reducer;
